import { useCallback } from 'react';
import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectVariablesByName, selectVariablesByTemplate } from 'components/editor/grid/reduxStore/allVariableSlice';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import type { CrmArrayLeaf } from './crmArrayPayload';
import type { CrmInstanceResolver } from './deriveCrmRows';
import { propagateParentCellToChildren } from './propagateParentCellToChildren';

const IDENTITY_ROWS = (rows: TableRowType[]): TableRowType[] => rows;

type UseCrmCellPropagationParams = {
  rowId: string;
  field: string;
};

/**
 * Returns a rows mapper that mirrors a manual edit of the pattern row into its
 * generated children before commit. Identity when the CRM embed flag is off.
 */
export function useCrmCellPropagation({ rowId, field }: UseCrmCellPropagationParams): (rows: TableRowType[]) => TableRowType[] {
  const isCrmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const variablesByName = useAppSelector(selectVariablesByName);
  const variablesByTemplate = useAppSelector(selectVariablesByTemplate);
  const resolveInstances: CrmInstanceResolver = useCallback(
    (fieldRef) => {
      const byTemplate = fieldRef.template ? variablesByTemplate[fieldRef.template] : undefined;
      const node = byTemplate ?? variablesByName[fieldRef.name];
      if (!node) return undefined;
      if (node.shape?.type === 'array' && node.variables?.length) return node.variables as CrmArrayLeaf[];
      return node.objectId ? [node as CrmArrayLeaf] : undefined;
    },
    [variablesByName, variablesByTemplate]
  );

  const propagate = useCallback(
    (rows: TableRowType[]) =>
      propagateParentCellToChildren({
        rows,
        parentRowId: rowId,
        columnField: field,
        resolveInstances,
      }),
    [rowId, field, resolveInstances]
  );

  return isCrmPricingEmbedEnabled ? propagate : IDENTITY_ROWS;
}
